import { useTranslation }                            from 'react-i18next'
import {
  Alert,
  Container, Divider,
  Grid, List,
  Stack, Typography
}                                         from '@mui/material'
import { useApi }                         from '../utils/api'
import React, { useEffect, useState }     from 'react'
import { fDateFromTo, fDateTime, fPrice } from '../utils/textutils'
import Page                               from '../ui/components/Page'
import { BaseBreadcrumbs }                from '../ui/components/BaseBreadcrumbs'
import { useParams }                      from 'react-router-dom'
import BaseTable                          from '../ui/components/base-table'
import { ClickableDataRow, DataRow }      from '../components/DataRow'
import ContentWidget                      from '../components/ContentWidget'
import PrintTransactionWidget             from './PrintTransactionWidget'
import Iconify                            from '../ui/components/Iconify'
import Consts                             from '../Consts'

export default function TransactionPage () {

  const { transactionId } = useParams()
  const { t } = useTranslation()
  const api = useApi()
  const [transaction, setTransaction] = useState(null)

  useEffect(() => {
    api.transaction.detail(transactionId).then(msg => {
      setTransaction(msg)
    }).catch(e => {
      console.error('Failed to load transaction', e)
    })
  }, [transactionId])

  const logColumns = [
    { field: 'created', headerName: t('Created'), width: 140, sortable: false, filterable: false, renderCell: (cell) => fDateTime(cell.row.created) },
    { field: 'action', headerName: t('Log action'), flex: 1, sortable: false, filterable: false },
    { field: 'message', headerName: t('Log message'), flex: 2, sortable: false, filterable: false },
  ];
  const transactionShareColumns = [
    { field: 'accounted', headerName: t('Accounted'), flex: 1, sortable: false, filterable: false },
    { field: 'type', headerName: t('Share type'), flex: 1, sortable: false, filterable: false, renderCell: (cell) => {
        return t(`transactionType_${cell.row.type}`)
    }},
    { field: 'price', headerName: t('Price'), flex: 1, sortable: false, filterable: false, renderCell: (cell) => {
      return fPrice(cell.row.price, 4)
    }},
  ];

  return (<Page title={`#${transactionId} ${t('Transaction')}`}>
    <Container maxWidth={'xl'}>
      <BaseBreadcrumbs key={'breadcrumb-transactions'}
                       title={`${t('Transaction')} #${transactionId}`}
                       items={[
                         { href: '/transactions', title: t('Transactions') }
                       ]}/>
      <Stack direction={'row'} sx={{ py: 2 }}>
        {transaction && <PrintTransactionWidget transaction={transaction} />}
      </Stack>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <ContentWidget title={t('Content')} content={transaction?.message?.content ?? ''} message={transaction?.message} />
        </Grid>
        <Grid item xs={12}>
          {transaction?.isDeactivated && <Alert icon={<Iconify icon={'material-symbols:cancel'} />} severity={'warning'}>
            {t('Subscription deactivated')}
          </Alert>}
        </Grid>
        <Grid item xs={12} md={6}>
          <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
            <ClickableDataRow icon={'material-symbols:service'} title={t('Service')} content={transaction?.service?.title} href={`/services/${transaction?.serviceId}`}/>
            <Divider component={'li'} />
            <ClickableDataRow icon={'material-symbols:service'} title={t('Pattern')} content={transaction?.serviceOperator?.pattern} href={`/services/${transaction?.serviceId}/operator/${transaction?.serviceOperatorId}`}/>
            <Divider component={'li'} />
            <DataRow title={`${t('PriceWithVat')} / ${t('Vat')} / ${t('PriceNoVat')}`} content={`${fPrice(transaction?.priceWithVat)} / ${fPrice(transaction?.vat)} / ${fPrice(transaction?.price)}`} icon={'material-symbols:euro'} />
            {transaction?.validFrom && transaction?.validTo && <>
              <Divider component={'li'} />
              <DataRow title={t('Validity')} content={`${fDateFromTo(transaction.validFrom, transaction.validTo)}`} icon={'material-symbols:timer'} />
            </> }
            {transaction?.renewAt && <>
              <Divider component={'li'} />
              <DataRow title={t('Renew at')} content={fDateTime(transaction.renewAt)} icon={'material-symbols:timer'} />
            </>}
          </List>
        </Grid>
        <Grid item xs={12} md={6}>
          <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
            <DataRow title={t('transaction uuid')} content={transaction?.uuid ?? ''} />
            <Divider component={'li'} />
            <DataRow title={t('transaction code')} content={transaction?.code ?? ''} />
            <Divider component={'li'} />
            <DataRow title={t('Invoice number')} content={transaction?.invoiceId ?? ''} />
          </List>
        </Grid>
      </Grid>
      <Grid item xs={12} mt={2}>
        <Typography variant={'h4'}>{t('Logs')}</Typography>
        {transaction?.messageId && <BaseTable
          columns={logColumns}
          loadCallback={async (query, page, pageSize) => {
            try {
              return await api.log.messageList(transaction?.messageId)
            } catch (e) {
              console.log('Failed to load transaction logs', e)
              return {
                data: [],
                totalCount: 0,
              }
            }
          }}
        />}
      </Grid>
      <Grid item xs={12} mt={2}>
        <Typography variant={'h4'}>{t('Transaction shares')}</Typography>
        {transaction?.id && <BaseTable
          columns={transactionShareColumns}
          loadCallback={async (query, page, pageSize) => {
            try {
              return await api.transactionShare.listByTransaction(transaction?.id)
            } catch (e) {
              console.log('Failed to load transaction shares', e)
              return {
                data: [],
                totalCount: 0,
              }
            }
          }}
        />}
      </Grid>
    </Container>
  </Page>)
}